import React from 'react';
import { Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { formatTS } from '../utils/time';

/**
 * What was written about an agent: their evaluations (questions with a note or
 * lost points, and comments), error submissions (questions marked as an error,
 * feedback or praise, or with a note, and comments) and coaching sessions
 * (request type, area, note, references and comments).
 *
 * Shows a section for each key present in `material`, so a caller that did not
 * ask for errors gets no Errors heading. Used by the comments panel on Generate
 * Recap and by Agent Summary's Generate Recap popup.
 */

const commentBox = { fontSize: 13, marginTop: 4, padding: '4px 8px', background: '#fafafa', borderRadius: 4 };
const ERROR_KIND_COLOR = { Error: 'red', Feedback: 'orange', Praised: 'green' };

function Comments({ list, prefix }) {
  return (list || []).map((c, i) => (
    <div key={`${prefix}${i}`} style={commentBox}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {c.UserName} &middot; {formatTS(c.CreatedAt)}
      </Typography.Text>
      <div style={{ whiteSpace: 'pre-wrap' }}>{c.Comment}</div>
    </div>
  ));
}

// Sections named across the records, counted once per record, so a repeated
// problem stands out before reading the detail.
function repeatedSections(records, pick) {
  const counts = {};
  records.forEach((r) => {
    new Set(r.Questions.filter(pick).map((q) => q.Section)).forEach((s) => { counts[s] = (counts[s] || 0) + 1; });
  });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]);
}

function SectionTags({ label, entries, total }) {
  if (!entries.length) return null;
  return (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{label}: </Typography.Text>
      {entries.map(([s, n]) => (
        <Tag key={s} color={n > 1 ? 'red' : 'orange'} style={{ marginBottom: 4 }}>{s}: {n} of {total}</Tag>
      ))}
    </div>
  );
}

function Evaluations({ evals }) {
  return (
    <>
      <Typography.Text strong>Evaluations ({evals.length})</Typography.Text>
      {!evals.length && <Typography.Text type="secondary" style={{ fontSize: 12 }}>None.</Typography.Text>}
      <SectionTags label="Sections that lost points" entries={repeatedSections(evals, (q) => q.Lost)} total={evals.length} />
      {evals.map((e) => (
        <div key={`e${e.ID}`} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 4 }}>
            <Link to={`/cases/${e.ID}`} target="_blank"><strong>{String(e.CallDate || '').slice(0, 10)}</strong></Link>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {e.FormName}{e.ScorePct != null && ` · ${e.ScorePct}%`}{e.EvaluatorName && ` · ${e.EvaluatorName}`}
            </Typography.Text>
          </div>
          {e.Questions.length === 0 && e.Comments.length === 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>No notes, comments or lost points.</Typography.Text>
          )}
          {e.Questions.map((q, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{q.Section} &rsaquo; </Typography.Text>
              {q.Question}
              {q.Answer && <Typography.Text type="secondary"> &mdash; {q.Answer}</Typography.Text>}
              {q.Lost && <Tag color="red" style={{ marginLeft: 6 }}>{q.Points}/{q.MaxPoints}</Tag>}
              {q.Note && <div style={{ whiteSpace: 'pre-wrap', marginLeft: 12, color: '#434343' }}>{q.Note}</div>}
            </div>
          ))}
          <Comments list={e.Comments} prefix="c" />
        </div>
      ))}
    </>
  );
}

function Errors({ errors }) {
  return (
    <>
      <Typography.Text strong style={{ marginTop: 8 }}>Errors ({errors.length})</Typography.Text>
      {!errors.length && <Typography.Text type="secondary" style={{ fontSize: 12 }}>None.</Typography.Text>}
      <SectionTags label="Sections with errors" entries={repeatedSections(errors, (q) => q.Kind === 'Error')} total={errors.length} />
      {errors.map((x) => (
        <div key={`x${x.ID}`} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 4 }}>
            <Link to={`/errors/${x.ID}`} target="_blank"><strong>{x.DateOfError || `#${x.ID}`}</strong></Link>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {[x.FormName, x.Campaign, x.SubmittedByName].filter(Boolean).join(' · ')}
            </Typography.Text>
          </div>
          {x.Questions.length === 0 && x.Comments.length === 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>No errors, feedback or notes.</Typography.Text>
          )}
          {x.Questions.map((q, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{q.Section} &rsaquo; </Typography.Text>
              {q.Question}
              {q.Kind && (
                <Tag color={ERROR_KIND_COLOR[q.Kind]} style={{ marginLeft: 6 }}>
                  {q.Kind}{q.Kind === 'Error' && q.Count > 1 ? ` x${q.Count}` : ''}
                </Tag>
              )}
              {q.Note && <div style={{ whiteSpace: 'pre-wrap', marginLeft: 12, color: '#434343' }}>{q.Note}</div>}
            </div>
          ))}
          <Comments list={x.Comments} prefix="xc" />
        </div>
      ))}
    </>
  );
}

function Coaching({ sessions }) {
  return (
    <>
      <Typography.Text strong style={{ marginTop: 8 }}>Coaching sessions ({sessions.length})</Typography.Text>
      {!sessions.length && <Typography.Text type="secondary" style={{ fontSize: 12 }}>None.</Typography.Text>}
      {sessions.map((s) => (
        <div key={`s${s.ID}`} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 4 }}>
            <Link to={`/coaching/sessions/${s.ID}`} target="_blank"><strong>#{s.ID} {s.Title}</strong></Link>
            <Tag>{s.Status ? s.Status.charAt(0).toUpperCase() + s.Status.slice(1) : '—'}</Tag>
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            {[s.RequestType && `Request Type: ${s.RequestType}`, s.Area && `Area: ${s.Area}`,
              s.CoachName && `Coach: ${s.CoachName}`, `Created ${formatTS(s.CreatedAt)}`].filter(Boolean).join(' · ')}
          </Typography.Text>
          {(s.References || []).length > 0 && (
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>References: </Typography.Text>
              {s.References.map((r, i) => (
                <React.Fragment key={`${r.Type}${r.ID}`}>
                  {i > 0 && ' · '}
                  <Link to={r.Type === 'evaluation' ? `/cases/${r.ID}` : `/errors/${r.ID}`} target="_blank">
                    {r.Type === 'evaluation' ? 'Evaluation' : 'Error'} #{r.ID}
                  </Link>
                </React.Fragment>
              ))}
            </div>
          )}
          {s.Note && <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', marginBottom: 4 }}>{s.Note}</div>}
          <Comments list={s.Comments} prefix="sc" />
        </div>
      ))}
    </>
  );
}

export default function RecapMaterial({ material }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {material.evaluations && <Evaluations evals={material.evaluations} />}
      {material.errors && <Errors errors={material.errors} />}
      {material.coaching && <Coaching sessions={material.coaching} />}
    </div>
  );
}

// The same material as plain text, for the Copy button.
export function recapMaterialText(material, heading = '') {
  const lines = heading ? [heading, ''] : [];
  const comments = (list) => (list || []).forEach((c) =>
    lines.push(`    ${c.UserName} (${formatTS(c.CreatedAt)}): ${c.Comment}`));
  if (material.evaluations) {
    lines.push(`EVALUATIONS (${material.evaluations.length})`);
    material.evaluations.forEach((e) => {
      lines.push(`- ${String(e.CallDate || '').slice(0, 10)} ${[e.FormName, e.ScorePct != null && `${e.ScorePct}%`, e.EvaluatorName].filter(Boolean).join(' · ')}`);
      e.Questions.forEach((q) => {
        lines.push(`  * ${q.Section} > ${q.Question}${q.Answer ? ` -- ${q.Answer}` : ''}${q.Lost ? ` (${q.Points}/${q.MaxPoints})` : ''}`);
        if (q.Note) lines.push(`    ${q.Note}`);
      });
      comments(e.Comments);
    });
    lines.push('');
  }
  if (material.errors) {
    lines.push(`ERRORS (${material.errors.length})`);
    material.errors.forEach((x) => {
      lines.push(`- ${x.DateOfError || `#${x.ID}`} ${[x.FormName, x.Campaign, x.SubmittedByName].filter(Boolean).join(' · ')}`);
      x.Questions.forEach((q) => {
        lines.push(`  * ${q.Section} > ${q.Question}${q.Kind ? ` (${q.Kind}${q.Kind === 'Error' && q.Count > 1 ? ` x${q.Count}` : ''})` : ''}`);
        if (q.Note) lines.push(`    ${q.Note}`);
      });
      comments(x.Comments);
    });
    lines.push('');
  }
  if (material.coaching) {
    lines.push(`COACHING SESSIONS (${material.coaching.length})`);
    material.coaching.forEach((s) => {
      lines.push(`- #${s.ID} ${s.Title} (${[s.RequestType, s.Area, s.Status, s.CoachName].filter(Boolean).join(' · ')}), created ${formatTS(s.CreatedAt)}`);
      if (s.Note) lines.push(`  ${s.Note}`);
      comments(s.Comments);
    });
  }
  return lines.join('\n').trim();
}
